import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations';
import { deepMerge } from '../utils/cms';

const CmsContext = createContext();
const STORAGE_KEY = 'amhbm_cms_data';
const API_BASE_URL = '';

export const useCms = () => {
  const context = useContext(CmsContext);
  if (!context) {
    throw new Error('useCms must be used within CmsProvider');
  }
  return context;
};

export const CmsProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/content`);
        const data = await response.json();
        setOverrides(data);
      } catch (e) {
        console.error("Failed to load CMS data from API", e);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const saveToStorage = async (newOverrides) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newOverrides),
      });
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) errorMessage = errorData.message;
          else if (errorData.error) errorMessage = errorData.error;
        } catch (jsonError) {
          console.error("Failed to parse error response", jsonError);
        }
        throw new Error(errorMessage);
      }
      
      setOverrides(newOverrides);
    } catch (e) {
      console.error("Failed to save CMS data", e);
      alert(`Failed to save changes: ${e.message}`);
    }
  };

  const getContent = (language) => {
    const base = translations[language] || translations.en;
    const langOverrides = overrides[language] || {};
    return deepMerge(base, langOverrides);
  };

  const updateContent = async (newOverrides) => {
    // Merge new overrides into existing ones
    const updated = deepMerge(overrides, newOverrides);
    saveToStorage(updated);
  };

  const updatePageContent = async (language, pageKey, pageContent) => {
    // Construct the nested object update
    // { [language]: { [pageKey]: pageContent } }
    const updateData = {
      [language]: {
        [pageKey]: pageContent
      }
    };
    const updated = deepMerge(overrides, updateData);
    saveToStorage(updated);
  };

  return (
    <CmsContext.Provider value={{ getContent, updateContent, updatePageContent, overrides, loading }}>
      {children}
    </CmsContext.Provider>
  );
};
